import { DatePipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { SiaUtilsApi } from '../apis/sia-utils.api';
import { SiaMenu } from '../interfaces/sia-menu';
import { SiaSessionParam } from '../interfaces/sia-session-param';
import { SiaMenuService } from './menu.service';
import { notify } from './notify';
import { SiaSessionService } from './session.service';

@Injectable({
  providedIn: 'root',
})
export class SiaUtilsService {
  private datePipe = inject(DatePipe);
  private utilsApi = inject(SiaUtilsApi);
  private sharedSession = inject(SiaSessionService);
  private menuService = inject(SiaMenuService);
  private sanitizer = inject(DomSanitizer);

  private _sidebarChanged: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private _gridInsertedRow: Subject<any> = new Subject<any>();

  private _otherElementsHeightSubject: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  private _refreshAskSubject: Subject<boolean> = new Subject<boolean>();

  private _errorSubject: BehaviorSubject<string> = new BehaviorSubject<string>(
    '',
  );

  get onSidebarChanged$(): Observable<boolean> {
    return this._sidebarChanged.asObservable();
  }

  get onGridInsertedRowChanged$(): Observable<any> {
    return this._gridInsertedRow.asObservable();
  }

  get onOtherElementsHeightChanged$(): Observable<number> {
    return this._otherElementsHeightSubject
      .asObservable()
      .pipe(distinctUntilChanged());
  }
  get onErrorCatched$(): Observable<string> {
    return this._errorSubject.asObservable();
  }

  get onRefreshAsk$(): Observable<boolean> {
    return this._refreshAskSubject.asObservable();
  }

  /** Inserted by Angular inject() migration for backwards compatibility */

  constructor() {}

  onSidebarChanged() {
    this._sidebarChanged.next(true);
  }

  askRefresh() {
    this._refreshAskSubject.next(false);
  }
  onOtherElementsHeightChanged(size: number) {
    this._otherElementsHeightSubject.next(size);
  }

  onGridInsertedRow(row?: any) {
    this._gridInsertedRow.next(row);
  }

  notifyErrorOnException(e: any, componentId: string) {
    if (e.error) {
      // console.error(e);
      notify(e.error.detail, 3000, 'error', undefined);
    } else {
      // console.error(e);
    }
    throwError(e);
  }

  formatDate(d: Date) {
    return this.datePipe.transform(d, 'dd/MM/yy');
  }

  getDateLastModified(): Promise<string> {
    var lastModifiedDate = this.sharedSession.getLastModifiedDate();
    if (!lastModifiedDate) {
      return this.utilsApi.getDateLastModified().then((res) => {
        if (res) {
          this.sharedSession.setLastModifiedDate(res);
          return Promise.resolve(res);
        }
        return Promise.resolve('');
      });
    } else {
      return Promise.resolve(lastModifiedDate);
    }
  }

  sendEmail(email: string) {
    if (email) {
      document.location.href = 'mailto:' + email;
    }
  }

  openUserCall(telephone: string) {
    if (telephone) {
      document.location.href = 'tel:' + telephone;
    }
  }
  hasFilterPanel(menuId?: string) {
    if (!menuId) {
      return Promise.resolve(false);
    }
    return this.menuService.getMenu(menuId).then((menu: SiaMenu | null) => {
      return menu?.filterPanelId ? true : false;
    });
  }
  /**
   * @ngdoc method
   * @name replaceArraSiaSessionParamValue
   * @description replace the value in a sessionParam array if the id match a record or add it
   * @param {Array[]} sessionParams the sessionParam
   * @param {String} id the param Id
   * @param {Object} value the value of the matching param
   * @returns {SiaSessionParam[]} the sessionParam with the new value
   */
  replaceArraSiaSessionParamValue(
    sessionParams: SiaSessionParam[],
    id: string,
    value: any,
  ) {
    var param = sessionParams.find((x) => x.id == id);
    if (param) {
      param.value = value;
    } else {
      sessionParams.push({ id: id, value: value });
    }
    return sessionParams;
  }

  arrayMove(arr: any, old_index: any, new_index: any) {
    if (new_index >= arr.length) {
      var k = new_index - arr.length + 1;
      while (k--) {
        arr.push(undefined);
      }
    }
    arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
    return arr; // for testing
  }
  execAction(data: string) {
    return this.utilsApi.execAction(data);
  }
  getTableData(schema: string, tableName: string) {
    let data = this.sharedSession.get(schema + '-' + tableName);
    if (data) return Promise.resolve(data);
    return this.utilsApi.getTableData(schema, tableName).then((data: any) => {
      if (!data) return;
      // this.sharedSession.set(schema + "-" + tableName, data);
      return data;
    });
  }

  convertBlob(blob: any) {
    if (!blob) return;
    if (blob.size === 0) return;
    let objectURL = URL.createObjectURL(blob);
    let c = this.sanitizer.bypassSecurityTrustUrl(objectURL);
    return c;
  }
}
